/**
 * 정오 표기 쓰기 뮤테이션·상세 캐시 헬퍼 (결정 G1·D2).
 *
 * @spec:2026-09-29-처방검토-판정기록-조회화면
 *
 * 훅은 API 경계만 책임진다 — PUT·DELETE 호출·봉투 언랩·오류 변환(DoseApiError)·캐시 헬퍼.
 * 줄 맥락이 필요한 분기(T6/T7/T18·Toast·줄 상태)는 페이지 몫이다(ARCHITECTURE §2 — 비즈니스 로직은 Page).
 * useDoseReviewLogs.ts(S5)는 수정하지 않는다 — 무효화 키는 doseReviewLogKeys와 같은 배열 접두 리터럴.
 */
import { useMutation, QueryClient } from "react-query";
import { DoseApiError } from "services/api/doseApiError";
import { doseReviewLogsApi } from "services/api/doseReviewLogs/doseReviewLogsApi";
import type {
  DoseReviewLogEnvelope,
  DoseReviewLogDetailData,
  DoseLabel,
  PutDoseLabelBody,
  PutDoseLabelData,
  DeleteDoseLabelData,
} from "services/api/doseReviewLogs/types";

/** 봉투 → data. error≠0 또는 data 없음이면 DoseApiError(6122 → data.label 보존, AC L1246). */
const call = async <T>(p: Promise<DoseReviewLogEnvelope<T>>): Promise<T> => {
  const res = await p;
  if (res.error !== 0 || res.data == null) {
    throw new DoseApiError({ code: res.error, message: res.message, data: res.data });
  }
  return res.data;
};

export const useDoseReviewLabelMutations = () => ({
  putLabel: useMutation((v: { logId: number; index: number; body: PutDoseLabelBody }) =>
    call<PutDoseLabelData>(doseReviewLogsApi.putLabel(v.logId, v.index, v.body))
  ),
  deleteLabel: useMutation((v: { logId: number; index: number; expectedRevision: number }) =>
    call<DeleteDoseLabelData>(doseReviewLogsApi.deleteLabel(v.logId, v.index, v.expectedRevision))
  ),
});

/**
 * 성공(T5·T9·T18) 시 캐시에 있는 **모든 상세**에서 같은 caseId 줄의 표기를 교체한다(결정 D2).
 * 표기는 case_id에 붙으므로([이전]/[다음]으로 본 다른 기록도) 재요청 0으로 일관을 맞춘다.
 */
export const applyLabelToDetailCaches = (
  qc: QueryClient,
  caseId: string,
  label: DoseLabel | null
): void => {
  qc.setQueriesData<DoseReviewLogDetailData | undefined>(["doseReviewLogs", "detail"], (d) =>
    d && d.items.some((it) => it.caseId === caseId)
      ? { ...d, items: d.items.map((it) => (it.caseId === caseId ? { ...it, label } : it)) }
      : d
  );
};

/** 성공(T5·T9) 시 neighbors·목록·요약을 무효화한다(D-12 — 정오 진행·labelState·순번이 바뀐다). */
export const invalidateAfterLabelChange = (qc: QueryClient): void => {
  qc.invalidateQueries(["doseReviewLogs", "list"]);
  qc.invalidateQueries(["doseReviewLogs", "summary"]);
  qc.invalidateQueries(["doseReviewLogs", "neighbors"]);
};
